import { AUTO_PAID_METHODS, RECURRENCE_MONTHS, TRACEABILITY_FROM, TRACEABLE_CATEGORIES, UNTRACEABLE_METHODS } from '@/function/expense/expense.tax.config';
import type { TypeExpense, TypeExpenseRebillState, TypeExpenseState } from '@/types/expense/types.expense';
import { addMonthsToDayKey, daysBetweenKeys, isDayKey, localDayKey } from '@/utils/date/day.key';

export const expenseCode = (expense: Pick<TypeExpense, 'number'>): string =>
    (expense.number.seq > 0 ? `SP ${expense.number.year}/${String(expense.number.seq).padStart(3, '0')}` : 'Prevista');

export const expensePlanned = (expense: Partial<Pick<TypeExpense, 'stage'>>): boolean => expense.stage === 'planned';

export const expensePlanDays = (expense: Pick<TypeExpense, 'plannedDate'>, today: string = localDayKey()): number | null =>
    (isDayKey(expense.plannedDate) ? daysBetweenKeys(today, expense.plannedDate) : null);

export const expenseState = (expense: Pick<TypeExpense, 'paidDate' | 'dueDate'>, today: string = localDayKey()): TypeExpenseState => {
    if (expense.paidDate) return 'paid';
    if (isDayKey(expense.dueDate) && expense.dueDate < today) return 'overdue';
    return 'due';
};

export const expenseRebillState = (expense: Pick<TypeExpense, 'rebillMode' | 'invoiceId'>): TypeExpenseRebillState => {
    if (expense.rebillMode === 'none') return 'none';
    return expense.invoiceId ? 'rebilled' : 'to-rebill';
};

export const expenseDaysLate = (expense: Pick<TypeExpense, 'paidDate' | 'dueDate'>, today: string = localDayKey()): number =>
    expenseState(expense, today) === 'overdue' ? daysBetweenKeys(expense.dueDate, today) : 0;

export const expenseNextRenewal = (
    expense: Pick<TypeExpense, 'recurrence' | 'documentDate'> & Partial<Pick<TypeExpense, 'renewedBy' | 'renewalNextDate' | 'renewalStopDate'>>,
): string | null => {
    const months = RECURRENCE_MONTHS[expense.recurrence] ?? 0;
    if (!months || !isDayKey(expense.documentDate) || expense.renewedBy) return null;
    const next = isDayKey(expense.renewalNextDate) ? expense.renewalNextDate : addMonthsToDayKey(expense.documentDate, months);
    return isDayKey(expense.renewalStopDate) && next >= expense.renewalStopDate ? null : next;
};

export const expenseStopped = (expense: Partial<Pick<TypeExpense, 'renewalStopDate'>>, today: string = localDayKey()): boolean =>
    isDayKey(expense.renewalStopDate) && expense.renewalStopDate <= today;

export const expenseRenewsPaid = (expense: Pick<TypeExpense, 'paidDate' | 'method'>): boolean =>
    Boolean(expense.paidDate) && AUTO_PAID_METHODS.includes(expense.method);

export const expenseMonthlyCents = (expense: Pick<TypeExpense, 'recurrence' | 'totalCents'>): number => {
    const months = RECURRENCE_MONTHS[expense.recurrence] ?? 0;
    return months ? Math.round(expense.totalCents / months) : 0;
};

export const expenseUntraceable = (expense: Pick<TypeExpense, 'category' | 'method' | 'abroad' | 'documentDate'>): boolean =>
    TRACEABLE_CATEGORIES.includes(expense.category)
    && UNTRACEABLE_METHODS.includes(expense.method)
    && !expense.abroad
    && isDayKey(expense.documentDate)
    && expense.documentDate >= TRACEABILITY_FROM;
